
'use client'

import { getStoredWalletSession, verifyStoredWalletSession, walletSessionStorageKeys } from './walletSessionClient.js'

const CHANNEL_PREFIX = 'ql7:qcoin-balance:'
const EVENT = 'qcoin:balance-changed'
const PRESENCE_INTERVAL_MS = 55 * 1000
const SETTLEMENT_INTERVAL_MS = 10 * 60 * 1000
const BOOTSTRAP_RETRY_MIN_MS = 15 * 1000
const BOOTSTRAP_RETRY_MAX_MS = 60 * 1000
const SETTLEMENT_RETRY_MIN_MS = 15 * 1000
const SETTLEMENT_RETRY_MAX_MS = 60 * 1000

let runtime = null
let runtimeOwners = 0
const listeners = new Set()

function nowPerf() {
  try { return performance.now() } catch { return Date.now() }
}

function validNumber(value, fallback = 0) {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

function compareAnchor(a = {}, b = {}) {
  const keys = ['balanceRevision', 'settlementVersion', 'lastAccrualAt', 'serverNow']
  for (const key of keys) {
    const av = validNumber(a?.[key], 0)
    const bv = validNumber(b?.[key], 0)
    if (av !== bv) return av > bv ? 1 : -1
  }
  return 0
}

function vipIntervalBalance(anchor, start, end) {
  if (end <= start) return 0
  const rate = validNumber(anchor?.incPerSec, 0)
  const vipStart = Date.parse(String(anchor?.vipWindow?.activeFromISO || ''))
  const vipEnd = Date.parse(String(anchor?.vipWindow?.untilISO || ''))
  let vipMs = 0
  if (Number.isFinite(vipEnd) && vipEnd > start && (!Number.isFinite(vipStart) || vipStart < end)) {
    vipMs = Math.max(0, Math.min(end, vipEnd) - Math.max(start, Number.isFinite(vipStart) ? vipStart : start))
  }
  const normalMs = Math.max(0, (end - start) - vipMs)
  return (normalMs / 1000) * rate + (vipMs / 1000) * rate * 2
}

export function projectQcoinAnchor(anchor = {}, perfNow = nowPerf()) {
  const base = { ...anchor }
  const durable = validNumber(base.durableBalance, validNumber(base.balance, 0))
  const projectionBase = validNumber(base.projectionBaseBalance, durable)
  const durableSeconds = validNumber(base.durableSeconds, validNumber(base.seconds, 0))
  const projectionBaseSeconds = validNumber(base.projectionBaseSeconds, durableSeconds)
  const serverNow = validNumber(base.serverNow, Date.now())
  const elapsed = Math.max(0, perfNow - validNumber(base.__receivedPerfAt, perfNow))
  const projectedAt = serverNow + elapsed
  const graceMs = Math.max(60_000, validNumber(base.graceMs, 4 * 60 * 60 * 1000))
  const liveCursor = validNumber(base.liveAccrualCursor, validNumber(base.lastAccrualAt, serverNow))
  const offline = validNumber(base.presenceOfflineAt, 0) >= validNumber(base.lastActiveAt, 0) && validNumber(base.presenceOfflineAt, 0) > 0
  const liveGap = Math.max(0, projectedAt - liveCursor)
  if (offline || base.paused || base.liveAccrualEligible === false || liveGap > graceMs) {
    return { ...base, balance: projectionBase, seconds: projectionBaseSeconds, projectedAt }
  }
  const delta = vipIntervalBalance(base, serverNow, projectedAt)
  const deltaSeconds = Math.max(0, Math.floor((projectedAt - serverNow) / 1000))
  return {
    ...base,
    balance: validNumber(base.balance, projectionBase) + delta,
    seconds: validNumber(base.seconds, projectionBaseSeconds) + deltaSeconds,
    projectedAt,
  }
}

function emit() {
  if (!runtime) return
  const snapshot = runtime.anchor ? projectQcoinAnchor(runtime.anchor) : null
  for (const fn of listeners) { try { fn(snapshot) } catch {} }
}


export function isQcoinAnchorVipActive(anchor = {}) {
  const at = validNumber(anchor?.projectedAt, validNumber(anchor?.serverNow, Date.now()))
  const from = Date.parse(String(anchor?.vipWindow?.activeFromISO || ''))
  const until = Date.parse(String(anchor?.vipWindow?.untilISO || ''))
  return Number.isFinite(until) && until > at && (!Number.isFinite(from) || from <= at)
}

export function buildQcoinClientAuthHeaders({ accountId = '', json = true } = {}) {
  const session = getStoredWalletSession()
  const headers = {}
  if (json) headers['content-type'] = 'application/json'
  if (session.token) headers['x-wallet-session-token'] = session.token
  if (session.walletAddress) headers['x-wallet-address'] = session.walletAddress
  const accountHint = String(accountId || session.accountId || '').trim()
  if (accountHint) headers['x-auth-account-id'] = accountHint
  try {
    const initData = String(window?.Telegram?.WebApp?.initData || '').trim()
    if (initData) headers['x-telegram-init-data'] = initData
  } catch {}
  return { headers, session, hasAuthority: Boolean(headers['x-wallet-session-token'] || headers['x-telegram-init-data']) }
}

function authHeaders() {
  return buildQcoinClientAuthHeaders({ json: true })
}

function walletSessionSignature() {
  const session = getStoredWalletSession()
  const token = String(session?.token || '').trim()
  const accountId = String(session?.accountId || session?.walletAddress || '').trim().toLowerCase()
  return token && accountId ? `${token}:${accountId}` : ''
}

async function reconcileWalletSessionAfterUnauthorized() {
  const session = getStoredWalletSession()
  if (!String(session?.token || '').trim()) return
  await verifyStoredWalletSession({ emitAuthTransition: false }).catch(() => null)
}

function createClientInstanceId() {
  let nonce = ''
  try { nonce = String(window.crypto?.randomUUID?.() || '').replace(/[^A-Za-z0-9_-]/g, '') } catch {}
  if (!nonce) nonce = `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`
  const id = `qtab_${nonce}`.slice(0, 64)
  // Diagnostic only: never reuse this value as authority after a reload. A new
  // document runtime must get a new lease even if sessionStorage was cloned by
  // a duplicated browser tab.
  try { window.sessionStorage?.setItem('ql7_qcoin_tab_instance_id', id) } catch {}
  return id
}
function ensureChannel(uid) {
  if (!runtime || typeof BroadcastChannel === 'undefined') return
  const cleanUid = String(uid || '').trim()
  if (!cleanUid || runtime.channelUid === cleanUid) return
  try { runtime.channel?.close?.() } catch {}
  runtime.channel = new BroadcastChannel(`${CHANNEL_PREFIX}${cleanUid}`)
  runtime.channelUid = cleanUid
  runtime.channel.onmessage = (event) => {
    // Cross-tab messages are hints only. Same-origin JS/DevTools must never be
    // able to inject a monetary anchor or revision into this runtime.
    if (event?.data?.type !== 'refresh') return
    if (document.visibilityState !== 'visible') {
      runtime.channelRefreshPending = true
      return
    }
    queueAuthoritativeChannelRefresh()
  }
}

function resetRuntimeIdentity() {
  if (!runtime) return
  clearTimers()
  if (runtime.authSyncTimer) clearTimeout(runtime.authSyncTimer)
  if (runtime.channelRefreshTimer) clearTimeout(runtime.channelRefreshTimer)
  runtime.authSyncTimer = null
  runtime.channelRefreshTimer = null
  runtime.channelRefreshPending = false
  runtime.authVersion += 1
  runtime.heartbeatPromise = null
  resetBootstrapRetry(runtime)
  resetSettlementRetry(runtime)
  runtime.anchor = null
  runtime.identityUid = ''
  runtime.walletSessionSignature = ''
  try { runtime.channel?.close?.() } catch {}
  runtime.channel = null
  runtime.channelUid = ''
  emit()
}

export function acceptQcoinAnchor(anchor, { broadcast = false, allowIdentitySwitch = false } = {}) {
  if (!runtime || !anchor || typeof anchor !== 'object') return false
  if (!authHeaders().hasAuthority) return false
  const next = { ...anchor, __receivedPerfAt: nowPerf() }
  const nextUid = String(next.userId || '').trim()
  if (runtime.identityUid && nextUid && runtime.identityUid !== nextUid && !allowIdentitySwitch) return false
  if (nextUid && (allowIdentitySwitch || !runtime.identityUid)) {
    runtime.identityUid = nextUid
    ensureChannel(nextUid)
  }
  if (runtime.anchor && compareAnchor(next, runtime.anchor) < 0) return false
  runtime.anchor = next
  emit()
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: { ...next, balance: projectQcoinAnchor(next).balance } })) } catch {}
  if (broadcast && runtime.channel) {
    try {
      runtime.channel.postMessage({
        type: 'refresh',
        balanceRevision: validNumber(next.balanceRevision, 0),
        settlementVersion: validNumber(next.settlementVersion, 0),
      })
    } catch {}
  }
  return true
}

function queueAuthoritativeChannelRefresh() {
  if (!runtime || runtime.channelRefreshTimer) return
  if (document.visibilityState !== 'visible') {
    runtime.channelRefreshPending = true
    return
  }
  const instance = runtime
  const minIntervalMs = 1_000
  const elapsed = Math.max(0, Date.now() - validNumber(instance.lastChannelRefreshAt, 0))
  const delay = Math.max(50, minIntervalMs - elapsed)
  instance.channelRefreshTimer = window.setTimeout(async () => {
    if (runtime !== instance) return
    instance.channelRefreshTimer = null
    if (document.visibilityState !== 'visible') {
      instance.channelRefreshPending = true
      return
    }
    instance.channelRefreshPending = false
    instance.lastChannelRefreshAt = Date.now()
    await apiGet().catch(() => null)
    if (runtime === instance) schedule()
  }, delay)
}

async function apiGet() {
  const { headers, hasAuthority } = authHeaders()
  if (!hasAuthority || !runtime) return null
  const authVersion = runtime.authVersion
  const res = await fetch('/api/qcoin/get', { headers, cache: 'no-store' })
  const body = await res.json().catch(() => null)
  if (res.status === 401) {
    await reconcileWalletSessionAfterUnauthorized()
    throw new Error(body?.error || 'qcoin_get_unauthorized')
  }
  if (!res.ok || !body?.ok || !body?.qcoinAnchor) throw new Error(body?.error || 'qcoin_get_failed')
  if (!runtime || runtime.authVersion !== authVersion) return null
  acceptQcoinAnchor(body.qcoinAnchor, { allowIdentitySwitch: true })
  runtime.walletSessionSignature = walletSessionSignature()
  resetBootstrapRetry(runtime)
  return body
}

async function heartbeat(mode = 'presence', keepalive = false) {
  if (!runtime) return null
  const instance = runtime

  if (!keepalive && instance.heartbeatPromise) {
    if (mode === 'presence') return instance.heartbeatPromise
    await instance.heartbeatPromise.catch(() => null)
    if (runtime !== instance) return null
  }

  const execute = async () => {
    if (runtime !== instance) return null
    const { headers, hasAuthority } = authHeaders()
    if (!hasAuthority || !instance.anchor) return null
    const authVersion = instance.authVersion
    instance.seq += 1
    headers['x-forum-client-id'] = instance.clientId
    const body = {
      mode,
      active: mode !== 'offline',
      offline: mode === 'offline',
      presenceSeq: instance.seq,
      presenceGeneration: validNumber(instance.anchor.presenceGeneration, 0),
    }
    const res = await fetch('/api/qcoin/heartbeat', { method: 'POST', headers, body: JSON.stringify(body), keepalive, cache: 'no-store' })
    const data = await res.json().catch(() => null)
    if (runtime !== instance || instance.authVersion !== authVersion) return null
    if (res.status === 401) {
      await reconcileWalletSessionAfterUnauthorized()
      throw new Error(data?.error || 'qcoin_heartbeat_unauthorized')
    }
    if (res.status === 409 && Number.isFinite(Number(data?.presenceGeneration))) {
      const serverGeneration = Number(data.presenceGeneration)
      const localGeneration = validNumber(instance.anchor?.presenceGeneration, 0)
      if (String(data?.error || '') !== 'stale_presence_sequence' || serverGeneration !== localGeneration) {
        await apiGet().catch(() => null)
      }
      return data
    }
    if (!res.ok || !data?.ok) throw new Error(data?.error || 'qcoin_heartbeat_failed')
    if (data.qcoinAnchor) acceptQcoinAnchor(data.qcoinAnchor, { broadcast: !!data.settled })
    return data
  }

  if (keepalive) return execute()
  const pending = execute()
  instance.heartbeatPromise = pending
  try {
    return await pending
  } finally {
    if (runtime === instance && instance.heartbeatPromise === pending) instance.heartbeatPromise = null
  }
}

function clearTimers() {
  if (!runtime) return
  clearTimeout(runtime.presenceTimer)
  clearTimeout(runtime.settlementTimer)
  runtime.presenceTimer = null
  runtime.settlementTimer = null
}

function resetBootstrapRetry(instance = runtime) {
  if (!instance) return
  instance.bootstrapRetryMs = BOOTSTRAP_RETRY_MIN_MS
}

function noteBootstrapRetry(instance = runtime) {
  if (!instance) return
  const current = Math.max(BOOTSTRAP_RETRY_MIN_MS, validNumber(instance.bootstrapRetryMs, BOOTSTRAP_RETRY_MIN_MS))
  instance.bootstrapRetryMs = Math.min(BOOTSTRAP_RETRY_MAX_MS, current * 2)
}

function resetSettlementRetry(instance = runtime) {
  if (!instance) return
  instance.settlementRetryAt = 0
  instance.settlementRetryMs = SETTLEMENT_RETRY_MIN_MS
}

function noteSettlementRetry(instance = runtime) {
  if (!instance) return
  const delay = Math.min(
    SETTLEMENT_RETRY_MAX_MS,
    Math.max(SETTLEMENT_RETRY_MIN_MS, validNumber(instance.settlementRetryMs, SETTLEMENT_RETRY_MIN_MS)),
  )
  instance.settlementRetryAt = Date.now() + delay
  instance.settlementRetryMs = Math.min(SETTLEMENT_RETRY_MAX_MS, delay * 2)
}

function settlementDelayMs(anchor) {
  const serverNow = validNumber(anchor?.serverNow, Date.now())
  const receivedAt = validNumber(anchor?.__receivedPerfAt, nowPerf())
  const projectedNow = serverNow + Math.max(0, nowPerf() - receivedAt)
  const cursor = validNumber(anchor?.lastAccrualAt, projectedNow)
  const resume = validNumber(anchor?.accrualResumeAt, 0)
  const pending = validNumber(anchor?.pendingOfflineSettlementAt, 0)
  // Pending recovery should not sit behind a fresh post-return cursor. A stale
  // durable cursor also deliberately makes boot schedule an early checkpoint.
  const dueCursor = pending > cursor ? cursor : Math.max(cursor, resume || cursor)
  const age = Math.max(0, projectedNow - dueCursor)
  return Math.max(5_000, SETTLEMENT_INTERVAL_MS - age)
}

function schedule() {
  if (!runtime) return
  clearTimers()
  if (document.visibilityState !== 'visible' || !authHeaders().hasAuthority) return

  if (!runtime.anchor) {
    const retryMs = Math.max(BOOTSTRAP_RETRY_MIN_MS, validNumber(runtime.bootstrapRetryMs, BOOTSTRAP_RETRY_MIN_MS))
    runtime.presenceTimer = setTimeout(async () => {
      const instance = runtime
      await apiGet().catch(() => null)
      if (runtime !== instance) return
      if (runtime?.anchor) {
        resetBootstrapRetry(runtime)
        await heartbeat('presence').catch(() => null)
      } else {
        noteBootstrapRetry(runtime)
      }
      schedule()
    }, retryMs)
    return
  }

  runtime.presenceTimer = setTimeout(async () => {
    await heartbeat('presence').catch(() => null)
    schedule()
  }, PRESENCE_INTERVAL_MS)

  const retryDelay = Math.max(0, validNumber(runtime.settlementRetryAt, 0) - Date.now())
  runtime.settlementTimer = setTimeout(async () => {
    try {
      const result = await heartbeat('settle')
      if (result?.ok === false) noteSettlementRetry(runtime)
      else resetSettlementRetry(runtime)
    } catch {
      noteSettlementRetry(runtime)
    }
    schedule()
  }, Math.max(settlementDelayMs(runtime.anchor), retryDelay))
}

export function startQcoinRuntime() {
  if (typeof window === 'undefined') return () => {}
  runtimeOwners += 1

  if (!runtime) {
    runtime = {
      anchor: null,
      clientId: createClientInstanceId(),
      seq: 0,
      channel: null,
      channelUid: '',
      identityUid: '',
      authVersion: 0,
      walletSessionSignature: '',
      authSyncTimer: null,
      channelRefreshTimer: null,
      channelRefreshPending: false,
      lastChannelRefreshAt: 0,
      presenceTimer: null,
      settlementTimer: null,
      displayTimer: null,
      heartbeatPromise: null,
      bootstrapRetryMs: BOOTSTRAP_RETRY_MIN_MS,
      settlementRetryAt: 0,
      settlementRetryMs: SETTLEMENT_RETRY_MIN_MS,
      cleanup: null,
    }
    const instance = runtime
    const syncAuth = async ({ reset = false } = {}) => {
      if (runtime !== instance) return
      if (reset) resetRuntimeIdentity()
      const { hasAuthority } = authHeaders()
      if (!hasAuthority || runtime !== instance) return
      await apiGet().catch(() => null)
      if (runtime !== instance) return
      await heartbeat('presence').catch(() => null)
      if (runtime === instance) schedule()
    }
    const onVisibility = async () => {
      if (runtime !== instance) return
      if (document.visibilityState === 'visible') {
        runtime.channelRefreshPending = false
        await heartbeat('presence').catch(() => apiGet().catch(() => null))
        if (runtime === instance) schedule()
      } else clearTimers()
    }
    const onPageHide = () => {
      if (runtime !== instance) return
      clearTimers()
      void heartbeat('offline', true).catch(() => null)
    }
    const onPageShow = async () => {
      if (runtime === instance && document.visibilityState === 'visible') await syncAuth()
    }
    const onNetworkOffline = () => { if (runtime === instance) clearTimers() }
    const onNetworkOnline = () => {
      if (runtime === instance && document.visibilityState === 'visible') void syncAuth()
    }
    const queueAuthSync = ({ sameSessionMayNoop = false } = {}) => {
      if (runtime !== instance) return
      if (sameSessionMayNoop && runtime.anchor) {
        const nextSignature = walletSessionSignature()
        if (nextSignature && nextSignature === runtime.walletSessionSignature) return
      }
      if (runtime.authSyncTimer) clearTimeout(runtime.authSyncTimer)
      runtime.authSyncTimer = window.setTimeout(() => { void syncAuth({ reset: true }) }, 40)
    }
    const onQcoinAuthReady = () => { queueAuthSync({ sameSessionMayNoop: true }) }
    const onAuth = () => { queueAuthSync() }
    const onLogout = () => { if (runtime === instance) resetRuntimeIdentity() }
    const sessionStorageKeys = new Set(Object.values(walletSessionStorageKeys || {}))
    const onStorage = (event) => {
      if (runtime !== instance || !sessionStorageKeys.has(String(event?.key || ''))) return
      queueAuthSync()
    }

    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('qcoin:auth-ready', onQcoinAuthReady)
    window.addEventListener('auth:ok', onAuth)
    window.addEventListener('auth:logout', onLogout)
    window.addEventListener('storage', onStorage)
    window.addEventListener('offline', onNetworkOffline)
    window.addEventListener('online', onNetworkOnline)
    runtime.displayTimer = window.setInterval(() => { if (runtime === instance && document.visibilityState === 'visible') emit() }, 1000)
    runtime.cleanup = () => {
      if (runtime !== instance) return
      clearTimers()
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('qcoin:auth-ready', onQcoinAuthReady)
      window.removeEventListener('auth:ok', onAuth)
      window.removeEventListener('auth:logout', onLogout)
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('offline', onNetworkOffline)
      window.removeEventListener('online', onNetworkOnline)
      if (instance.authSyncTimer) window.clearTimeout(instance.authSyncTimer)
      if (instance.channelRefreshTimer) window.clearTimeout(instance.channelRefreshTimer)
      if (instance.displayTimer) window.clearInterval(instance.displayTimer)
      try { instance.channel?.close?.() } catch {}
      runtime = null
    }

    void (async () => {
      if (document.visibilityState === 'visible') {
        await apiGet().catch(() => null)
        if (runtime !== instance) return
        if (runtime.anchor) await heartbeat('presence').catch(() => null)
      }
      if (runtime === instance) schedule()
    })()
  }

  let released = false
  return () => {
    if (released) return
    released = true
    runtimeOwners = Math.max(0, runtimeOwners - 1)
    if (runtimeOwners === 0) runtime?.cleanup?.()
  }
}

export function subscribeQcoinRuntime(listener) {
  if (typeof listener !== 'function') return () => {}
  listeners.add(listener)
  if (runtime?.anchor) { try { listener(projectQcoinAnchor(runtime.anchor)) } catch {} }
  return () => listeners.delete(listener)
}

export function getQcoinRuntimeSnapshot() {
  return runtime?.anchor ? projectQcoinAnchor(runtime.anchor) : null
}
